/**
 * @file Constantes et helpers d'affichage des niveaux de fidélité client.
 * @module components.customer.constants.statusConstants
 *
 * Les niveaux de fidélité sont désormais PERSONNALISABLES par établissement
 * (libellé, seuil, icône, couleur) via CustomerConfig côté backend. Ce fichier
 * fournit :
 * - les valeurs PAR DÉFAUT (repli quand la config n'est pas encore chargée ou
 *   indisponible) — strictement alignées sur les défauts backend ;
 * - buildStatusVisuals() qui transforme une liste de paliers (config ou
 *   défauts) en map d'affichage + options de filtre ;
 * - les listes de choix (icônes, couleurs) pour l'éditeur de réglages.
 *
 * La clé du niveau (new/occasional/loyal/privileged) est stable et calculée
 * côté backend (customer.statusLevel). Ce module ne calcule jamais le niveau,
 * il en dérive uniquement l'apparence.
 */

/**
 * Couleur de badge par défaut (gris neutre).
 */
export const DEFAULT_TIER_COLOR = '#919EAB';

/**
 * Longueur maximale du libellé d'un statut (doit refléter STATUS_LABEL_MAX_LENGTH
 * côté backend).
 */
export const STATUS_LABEL_MAX_LENGTH = 15;

/**
 * Normalise une couleur de badge en hexadécimal : renvoie un hex tel quel,
 * sinon la couleur par défaut.
 *
 * @param {string} color - Couleur hexadécimale (#RGB ou #RRGGBB)
 * @return {string} Couleur hexadécimale
 */
export function normalizeStatusColor(color) {
  return typeof color === 'string' && color.startsWith('#') ? color : DEFAULT_TIER_COLOR;
}

/**
 * Paliers par défaut (mêmes valeurs que le backend).
 * Forme alignée sur CustomerConfig.statusLevels.tiers.
 */
export const DEFAULT_STATUS_TIERS = [
  {key: 'new', label: 'Nouveau', minCount: 0, icon: 'mdi:party-popper', color: '#196f89', enabled: true, showLabel: true},
  {key: 'occasional', label: 'Occasionnel', minCount: 2, icon: 'mdi:star', color: '#00B8D9', enabled: true, showLabel: true},
  {key: 'loyal', label: 'Fidèle', minCount: 4, icon: 'mdi:heart', color: '#FF5630', enabled: true, showLabel: true},
  {key: 'privileged', label: 'Privilégié', minCount: 9, icon: 'mdi:crown', color: '#FFAB00', enabled: true, showLabel: true},
];

/**
 * Sources d'activité comptabilisées par défaut.
 */
export const DEFAULT_COUNT_SOURCES = {
  restaurant: true,
  wellness: true,
  vouchers: true,
};

/**
 * Sélection curée d'icônes Iconify (kit Material Design) proposées dans
 * l'éditeur de statuts. Jeu pertinent pour le statut/la fidélité client.
 */
export const STATUS_ICON_CHOICES = [
  'mdi:account',
  'mdi:account-star',
  'mdi:star',
  'mdi:heart',
  'mdi:crown',
  'mdi:medal',
  'mdi:trophy',
  'mdi:diamond-stone',
  'mdi:fire',
  'mdi:lightning-bolt',
  'mdi:shield-star',
  'mdi:star-circle',
  'mdi:gift',
  'mdi:check-decagram',
  'mdi:thumb-up',
  'mdi:rocket-launch',
  'mdi:party-popper',
  'mdi:white-balance-sunny',
];

/**
 * Construit un libellé de tooltip décrivant la plage d'interactions d'un palier.
 *
 * @param {Object} tier - Palier courant ({label, minCount})
 * @param {Object} [next] - Palier suivant (pour borner la plage haute)
 * @return {string} Tooltip lisible
 */
function buildTierTooltip(tier, next) {
  // Palier de base : plafonné à la première interaction (miroir du backend,
  // BASE_TIER_MAX_COUNT dans statusDefaults), quel que soit le seuil suivant.
  if (tier.minCount <= 0) {
    return `${tier.label} — première interaction`;
  }
  if (next) {
    const upper = next.minCount - 1;
    if (upper <= tier.minCount) {
      return `${tier.label} — ${tier.minCount} interaction(s)`;
    }
    return `${tier.label} — de ${tier.minCount} à ${upper} interactions`;
  }
  return `${tier.label} — ${tier.minCount} interaction(s) et plus`;
}

/**
 * Dérive la configuration visuelle des niveaux à partir d'une liste de paliers.
 *
 * @param {Array<Object>} [tiers] - Paliers de l'établissement (sinon défauts)
 * @return {{levelMap: Object, filterOptions: Array, getConfig: Function}}
 *   - levelMap : map clé de niveau -> {level, label, color, icon, tooltip}
 *   - filterOptions : options pour le filtre de liste (avec 'Tous' en tête)
 *   - getConfig(level) : config visuelle d'un niveau (repli sur le niveau de base)
 */
export function buildStatusVisuals(tiers) {
  const all = (Array.isArray(tiers) && tiers.length > 0 ? tiers : DEFAULT_STATUS_TIERS)
      .slice()
      .sort((a, b) => a.minCount - b.minCount);

  // Seuls les statuts actifs sont mappés (badge), proposés en filtre et bornent
  // les plages des tooltips. Un statut désactivé n'apparaît nulle part.
  const active = all.filter((tier) => tier.enabled !== false);

  const levelMap = {};
  active.forEach((tier, index) => {
    levelMap[tier.key] = {
      level: tier.key,
      label: tier.label,
      color: normalizeStatusColor(tier.color),
      icon: tier.icon || null,
      showLabel: tier.showLabel === true,
      tooltip: buildTierTooltip(tier, active[index + 1]),
    };
  });

  const filterOptions = [{value: 'all', label: 'Tous'}, ...active.map((tier) => ({value: tier.key, label: tier.label}))];

  return {
    levelMap,
    filterOptions,
    // Niveau absent/inconnu/désactivé → null (pas de badge).
    getConfig: (level) => (level ? levelMap[level] || null : null),
  };
}
